import {Box} from '@mui/material';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import AurPageHeader from '../../common/aur-page-header/AurPageHeader';
import AurClientDetailsView from '../../client-management/aur-client-details-view/AurClientDetailsView';
import {t} from 'i18next';
import {getClientById} from '../../../api/ClientManagementService';
import {
  setClientDetailData,
  TriggerProjectsAndSummary,
} from '../../../redux/slices/clientListSlice';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import AurProjectListContainer from '../../client-details/project-data-table/AurProjectListContainer';
import InfographicContainer from '../../client-details/infographic-panel/InfographicContainer';
import styles from '../../../styles/components/clientdetails/clientDetailsStyle.module.scss';
import ProjectDrawer from '../../client-details/project-drawer-panel/ProjectDrawer';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {IHeaderSummaryData} from '../../client-details/project-data-table/types';

import {ONLY_DATE_SHOW_FORMAT} from '../../../constants/constants';
import {useLocation} from 'react-router-dom';

const ClientDetails = () => {
  const clientDetail = useSelector(
    (state: RootState) => state.clientList.clientDetail
  );
  const dispatch = useDispatch();
  const [isGetClientByIdLoading, setIsGetClientByIdLoading] = useState(false);
  const [triggerClientUpdate, setTriggerClientUpdate] = useState(false);

  const clientId = useSelector(
    (state: RootState) => state.clientList.selectedClientId
  );
  const location = useLocation();

  // To fetch client details by Id and update the local store
  const fetchClientDetails = async () => {
    setIsGetClientByIdLoading(true);
    await getClientById(clientId)
      .then((res) => {
        const response = res.data?.data;
        const headerSummary = response?.headerSummary;
        dispatch(
          setClientDetailData({
            clientId: response.clientId,
            clientCode: response.clientCode,
            consilioClientId: response.consilioClientId,
            clientName: response.name,
            createdBy: response.createdBy,
            createdOn: response.createTime,
            budgetConsumptionEnabled: !!response?.budgetConsumptionEnabled,
            branches: response.branches.map(
              (branch: {branchId: string; branchName: string}) => ({
                branchId: branch.branchId,
                branchName: branch.branchName,
              })
            ),
            departments: response.departments?.map(
              (department: {
                departmentId: string;
                departmentName: string;
                description: string;
                createdBy: string;
                createdOn: string;
              }) => ({
                departmentId: department.departmentId,
                departmentName: department.departmentName,
                description: department.description,
                createdBy: department.createdBy,
                createdOn: department.createdOn,
              })
            ),
            headerSummary: {
              totalDepartments: headerSummary.totalDepartments,
              totalProjects: headerSummary.totalProjects,
              totalPermissionGroups: headerSummary.totalPermissionGroups,
              totalWorkspaces: headerSummary.totalWorkspaces,
              totalBranches: headerSummary.totalBranches,
            },
          })
        );
      })
      .catch((err) => {
        console.error('Error:', err);
      })
      .finally(() => {
        setIsGetClientByIdLoading(false);
      });
  };

  useEffect(() => {
    dispatch(TriggerProjectsAndSummary());
  }, [clientDetail]);

  useEffect(() => {
    fetchClientDetails();
  }, []);

  useEffect(() => {
    if (triggerClientUpdate) {
      fetchClientDetails();
      setTriggerClientUpdate(false);
    }
  }, [triggerClientUpdate]);

  const onUpdateDrawer = () => {
    setTriggerClientUpdate(true);
  };

  return (
    <>
      <Box>
        <AurPageHeader
          heading={t('client.details')}
          linkLabel={
            location?.state?.from == 'manage-dropdown'
              ? t('reports.projectOverview.label')
              : t('client.manageClients')
          }
          className="client-details-header"
          hideHeader={true}
          path={
            location?.state?.from == 'manage-dropdown'
              ? '/welcome-single-client'
              : '/clients'
          }
        />
        <AurClientDetailsView
          clientName={clientDetail.clientName}
          clientId={clientDetail.clientId}
          clientCode={clientDetail.clientCode}
          displayText={clientDetail.clientName.charAt(0).toUpperCase()}
          createdBy={clientDetail.createdBy}
          createdOn={getUTCDateTimeForDisplay(
            clientDetail.createdOn,
            ONLY_DATE_SHOW_FORMAT
          )}
          onUpdateDrawerParent={onUpdateDrawer}
        />
        {/* Infographic panel */}
        <Box className={`${styles.infographicContainer}`}>
          <InfographicContainer
            headerSummary={clientDetail.headerSummary as IHeaderSummaryData}
          />
        </Box>

        <AurProjectListContainer />
        <ProjectDrawer />
      </Box>{' '}
      {<AurLoadingIndicator overlay={true} isOpen={isGetClientByIdLoading} />}
    </>
  );
};

export default ClientDetails;
